import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const AdminEmailTemplatesPage = () => {
  const { sequenceId } = useParams();
  const { user } = useAuth();
  const [sequence, setSequence] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiCall = async (url, options = {}) => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  useEffect(() => {
    if (sequenceId) {
      loadSequenceWithTemplates();
    }
  }, [sequenceId]);

  const loadSequenceWithTemplates = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/v1/admin/email-sequences/sequences/${sequenceId}`);
      setSequence(data.sequence);
      setTemplates(data.sequence.templates || []);
    } catch (err) {
      setError(`Failed to load sequence: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await apiCall(`/api/v1/admin/email-sequences/templates/${templateId}`, {
        method: 'DELETE'
      });
      
      // Refresh templates
      await loadSequenceWithTemplates();
    } catch (err) {
      setError(`Failed to delete template: ${err.message}`);
    }
  };

  return (
    <div className="space-y-8">
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Email Templates</h1>
              {sequence && (
                <p className="text-sm text-muted-foreground mt-1">Sequence: {sequence.name}</p>
              )}
            </div>
            <Link to="/admin/email-sequences" className="btn btn-secondary">
              ← Back to Sequences
            </Link>
          </div>
        </div>

        <div className="card-body">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="text-red-600 text-sm">{error}</div>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading templates...</p>
            </div>
          ) : sequence ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-foreground">Templates ({templates.length})</h3>
                <Link 
                  to={`/admin/email-sequences/${sequenceId}/templates/create`} 
                  className="btn btn-primary"
                >
                  Create New Template
                </Link>
              </div>

              {templates.length > 0 ? (
                <div className="bg-card rounded-lg border border-border overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-background">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Step</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Subject</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Delay</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-gray-200">
                      {templates
                        .sort((a, b) => a.step_number - b.step_number)
                        .map((template) => (
                        <tr key={template.id} className="hover:bg-background">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                            {template.step_number}
                          </td>
                          <td className="px-6 py-4 text-sm text-foreground">
                            {template.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-muted-foreground">
                            {template.subject}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {template.delay_days} day{template.delay_days !== 1 ? 's' : ''}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              template.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {template.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                            <Link
                              to={`/admin/email-sequences/${sequenceId}/templates/${template.id}/edit`}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => deleteTemplate(template.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-4xl mb-4">📝</div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No Templates</h3>
                  <p className="text-muted-foreground mb-4">Create your first email template for this sequence.</p>
                  <Link 
                    to={`/admin/email-sequences/${sequenceId}/templates/create`} 
                    className="btn btn-primary"
                  >
                    Create First Template
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-4xl mb-4">⚠️</div>
              <h3 className="text-lg font-medium text-foreground mb-2">Sequence Not Found</h3>
              <p className="text-muted-foreground mb-4">The requested email sequence could not be found.</p>
              <Link to="/admin/email-sequences" className="btn btn-primary">
                Back to Sequences
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplatesPage;